import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "@/lib/router-compat";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { SidebarArrowTrigger } from "@/components/panel/SidebarArrowTrigger";
import PanelSidebar from "@/components/panel/PanelSidebar";
import GoSidebar, { GO_TRIAL_EVENT } from "@/components/public/GoSidebar";
import AuthModal from "@/components/LazyAuthModal";
import { useLanguage } from "@/i18n/LanguageContext";
import { useLanguageSwitch } from "@/hooks/useLanguageSwitch";
import { useAuthState } from "@/hooks/useAuthState";
import { useHasActivePanel } from "@/hooks/useHasActivePanel";
import { getSupabase } from "@/lib/supabase-lazy";
import { getPanelUrl } from "@/lib/panel-url";
import { useRouterState } from "@tanstack/react-router";
import { PUBLIC_TOOLS, PUBLIC_TOOL_TAB, publicToolKeyForTab, publicToolPath, type PublicToolKey } from "@/lib/publicToolRoutes";
import NotificationBell from "@/components/NotificationBell";
import CalendarHeaderButton from "@/components/roadmap/CalendarHeaderButton";
import PlanStatusChip from "@/components/PlanStatusChip";
import AdminHeaderControls from "@/components/plan/AdminHeaderControls";
import { cn } from "@/lib/utils";
import { CircleQuestionMark } from "lucide-react";
import UpgradePlansMenu from "@/components/plan/UpgradePlansMenu";
import { usePlanUi } from "@/hooks/usePlanUi";

const SIDEBAR_DEFAULT_PX = 288;

/** Permite al menú antiguo (`Header`) no pintarse dentro de esta envoltura. */
const PublicShellContext = createContext(false);
export const useInPublicShell = () => useContext(PublicShellContext);
const GUEST_STORE_KEY = "rl-guest-store-name";

interface ShellProfile {
  firstName: string;
  lastName: string;
  companyName: string;
  retailSector: string;
}

/**
 * Envoltura de todas las páginas públicas: reproduce la estructura de la
 * portada (menú lateral a la izquierda + barra superior) para que la web
 * tenga una única navegación. El contenido de cada página se pasa como hijo.
 * Con sesión activa el menú y la barra pasan a modo logueado ("Ir al Panel").
 */
const PublicShell = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const { switchLang } = useLanguageSwitch();
  const es = lang === "es";
  const { isAuthenticated, user } = useAuthState();
  const { hasPanel, isAdmin: isAdminView } = useHasActivePanel();
  const { isAdmin } = useHasActivePanel({ ignoreAdminView: true });

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<"login" | "signup">("signup");
  const [storeName, setStoreName] = useState("");
  const [profile, setProfile] = useState<ShellProfile | null>(null);

  // Perfil ligero solo cuando hay sesión: nombre del negocio y sector para el menú.
  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      setProfile(null);
      return;
    }
    let cancelled = false;
    void getSupabase().then(async (supabase) => {
      const { data } = await supabase
        .from("profiles")
        .select("first_name, last_name, company_name, retail_sector")
        .eq("user_id", user.id)
        .maybeSingle();
      if (cancelled) return;
      setProfile({
        firstName: data?.first_name ?? "",
        lastName: data?.last_name ?? "",
        companyName: data?.company_name ?? "",
        retailSector: data?.retail_sector ?? "",
      });
    }).catch(() => { /* el menú sigue funcionando sin perfil */ });
    return () => { cancelled = true; };
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    if (isAuthenticated && authOpen) setAuthOpen(false);
  }, [isAuthenticated, authOpen]);

  const goToPanel = () => {
    // El panel abre siempre en la Hoja de Ruta.
    const url = getPanelUrl("/dashboard?tab=roadmap");
    if (url.startsWith("http")) window.location.href = url;
    else navigate(url);
  };

  const planLabel = !isAuthenticated
    ? ""
    : hasPanel || isAdmin
      ? "Club Pro"
      : es ? "Plan Básico" : "Basic plan";

  // En /go el menú arranca siempre plegado y no altera la preferencia de Home A.
  const goPath = typeof window !== "undefined" && /^\/(en\/)?go\/?$/.test(window.location.pathname);
  useEffect(() => {
    try {
      const saved = localStorage.getItem("rl-panel-sidebar");
      if (goPath) setSidebarOpen(false);
      else if (saved) setSidebarOpen(saved === "open");
    } catch {
      /* almacenamiento no disponible */
    }
    try {
      setStoreName(localStorage.getItem(GUEST_STORE_KEY) || "");
    } catch {
      /* almacenamiento no disponible */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (goPath) return;
    try {
      localStorage.setItem("rl-panel-sidebar", sidebarOpen ? "open" : "closed");
    } catch {
      /* almacenamiento no disponible */
    }
  }, [sidebarOpen, goPath]);

  const homePath = es ? "/" : "/en";
  const planUi = usePlanUi();
  const [guideAvailable, setGuideAvailable] = useState(false);
  useEffect(() => {
    const on = (e: Event) => setGuideAvailable(Boolean((e as CustomEvent).detail));
    window.addEventListener("rl:guide-available", on);
    return () => window.removeEventListener("rl:guide-available", on);
  }, []);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isGo = /^\/(en\/?)?$/.test(pathname) || /^\/(en\/)?go\/?$/.test(pathname);
  // Limpia la marca antigua que arrastraba el menú Go a otras páginas.
  useEffect(() => {
    try { sessionStorage.removeItem("rl-go-origin"); } catch { /* sin almacenamiento */ }
  }, []);
  const currentTool = (Object.keys(PUBLIC_TOOLS) as PublicToolKey[]).find(
    (k) => PUBLIC_TOOLS[k].pathEs === pathname || PUBLIC_TOOLS[k].pathEn === pathname,
  );
  // El menú reducido es exclusivo de /go; el resto de la web usa siempre el completo.
  const isEditorial = /^\/(en\/)?editorial\/?$/.test(pathname);
  // Portada pública: menú replegado en miniatura al entrar.
  useEffect(() => { if (isGo && !isAuthenticated) setSidebarOpen(false); }, [isGo, isAuthenticated]);
  const useGoMenu = !isAuthenticated && !isEditorial;
  const shellActiveTab = currentTool ? PUBLIC_TOOL_TAB[currentTool] : "";
  const goToTab = (tab: string) => {
    const key = publicToolKeyForTab(tab);
    navigate(key ? publicToolPath(key, es ? "es" : "en") : `${homePath}?tab=${tab}`);
  };
  const openAuth = (mode: "login" | "signup") => {
    setAuthMode(mode);
    setAuthOpen(true);
  };

  return (
    <PublicShellContext.Provider value={true}>
    <SidebarProvider
      open={sidebarOpen}
      onOpenChange={setSidebarOpen}
      style={{ "--sidebar-width": `${SIDEBAR_DEFAULT_PX}px`, ...(useGoMenu ? { "--sidebar-width-icon": "4.5rem" } : {}) } as React.CSSProperties}
    >
      <div className="min-h-screen bg-background flex w-full">
        {useGoMenu ? (
          <GoSidebar lang={es ? "es" : "en"} isAuthenticated={isAuthenticated} onPanel={goToPanel} onLogin={() => openAuth("login")} />
        ) : (
        <PanelSidebar
          activeTab={shellActiveTab}
          onSelectTab={goToTab}
          lang={es ? "es" : "en"}
          progress={0}
          storeName={isAuthenticated ? (profile?.companyName || profile?.firstName || storeName) : storeName}
          planLabel={planLabel}
          {...(isAuthenticated
            ? {
                ...(profile?.retailSector ? { retailSector: profile.retailSector } : {}),
                isAdmin: isAdminView,
                showAdminLink: isAdmin,
                onSeeMentoring: goToPanel,
                onScheduleSession: goToPanel,
              }
            : {
                publicMode: true,
                onSetStoreName: (name: string) => {
                  setStoreName(name);
                  try {
                    if (name) localStorage.setItem(GUEST_STORE_KEY, name);
                    else localStorage.removeItem(GUEST_STORE_KEY);
                  } catch {
                    /* almacenamiento no disponible */
                  }
                },
                onRegister: () => openAuth("signup"),
                onLogin: () => openAuth("login"),
                onJoinPro: () => navigate(es ? "/tarifas" : "/en/pricing"),
                onGoHomeSection: (anchor: string) => {
                  window.location.assign(`${homePath}#${anchor}`);
                },
              })}
          onSetLang={(l) => switchLang(l)}
        />
        )}


        <div className="relative flex-1 min-w-0 flex flex-col">
          <div className={cn(
            "sticky top-0 z-30 flex items-center gap-2 border-b border-border bg-card/90 px-2 py-2 backdrop-blur-md sm:px-3",
            isGo && "absolute inset-x-0 border-0 bg-transparent backdrop-blur-none [&>div:first-child]:text-background [&>div:first-child_span]:text-background [&_[data-go-login]]:!text-background [&_[data-go-login]]:[text-shadow:0_1px_6px_rgb(0_0_0/0.4)] [&>div:first-child_span]:[text-shadow:0_1px_6px_rgb(0_0_0/0.4)]",
          )}>
            <div className={cn("flex min-w-0 items-center", isGo && "max-lg:gap-1 max-lg:rounded-full max-lg:bg-foreground/45 max-lg:px-1 max-lg:py-1 max-lg:backdrop-blur-sm max-lg:ring-1 max-lg:ring-background/15")}>
              <SidebarArrowTrigger label={es ? "Mostrar u ocultar el menú" : "Show or hide the menu"} />
              <span className={cn(
                "truncate font-display text-sm font-bold tracking-tight sm:text-base",
                isAdmin ? "hidden" : "inline",
                isGo && !isAuthenticated && "hidden",
              )}>
                {isAuthenticated && profile?.firstName
                  ? `${es ? "Hola" : "Hi"}, ${profile.firstName}`
                  : "Managing Retail, Made Simple"}
              </span>
            </div>
             <div className="ml-auto flex min-w-0 flex-1 items-center justify-end gap-2">
               {isAuthenticated ? (
                 <>
                   {!pathname.startsWith("/cuenta") && !pathname.startsWith("/en/account") && <Button
                     size="sm"
                     onClick={goToPanel}
                     className="hidden rounded-full font-body text-xs font-bold sm:inline-flex"
                   >
                     {es ? "Ir al Panel" : "Go to Panel"}
                   </Button>}
                   {isAdmin ? (
                      <AdminHeaderControls
                       onBackOffice={() => navigate("/admin")}
                     />
                   ) : <PlanStatusChip hasPro={hasPanel} showAlert={!hasPanel} />}
                   {planUi.active && !isAdmin && <UpgradePlansMenu />}
                   {user?.id && <CalendarHeaderButton userId={user.id} />}
                   {guideAvailable && (
                     <button
                       type="button"
                       onClick={() => window.dispatchEvent(new Event("rl:open-guide"))}
                       aria-label={es ? "Guía paso a paso" : "Step-by-step guide"}
                       title={es ? "Guía paso a paso" : "Step-by-step guide"}
                       className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-xs transition-colors hover:bg-secondary"
                     >
                       <CircleQuestionMark className="h-5 w-5" strokeWidth={1.5} />
                     </button>
                   )}
                   <NotificationBell />
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => openAuth("login")}
                    className={cn("hidden font-body text-xs font-semibold transition-colors sm:inline", isGo ? "text-background [text-shadow:0_1px_6px_rgb(0_0_0/0.45)] hover:opacity-80" : "text-muted-foreground hover:text-foreground")}
                  >
                    {es ? "Iniciar sesión" : "Log in"}
                  </button>
                  <Button
                    size="sm"
                    onClick={() => (isGo ? window.dispatchEvent(new Event(GO_TRIAL_EVENT)) : openAuth("signup"))}
                    className="rounded-full font-body text-xs font-bold"
                  >
                    {es ? "Acceso anticipado" : "Early access"}
                  </Button>
                </>
              )}
            </div>
          </div>

          {children}
        </div>
      </div>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} initialMode={authMode} />
    </SidebarProvider>
    </PublicShellContext.Provider>
  );
};

export default PublicShell;
